/**
 * Geo Data Service
 *
 * Queries the geo_countries, geo_states, and geo_cities collections in the
 * Appwrite brain database for the cascading Country → State → City selectors.
 * All queries use string attributes (country_code, state_code) which Appwrite
 * handles reliably.
 *
 * States without cities are not seeded, so every state in the dropdown will
 * produce cities when selected.
 */

import { Client, Databases, Query } from 'appwrite';
import { createAsyncRequestCache } from '../utils/asyncRequestCache';
import { normalizeLocale } from '../constants/locales';

const DATABASE_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID || '693c1ae3003d56af4154';
const COLLECTION_COUNTRIES = 'geo_countries';
const COLLECTION_STATES = 'geo_states';
const COLLECTION_CITIES = 'geo_cities';

const client = new Client()
  .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT || '')
  .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID || '');

const databases = new Databases(client);

/** One timezone entry inside the geo_countries `timezones` JSON column. */
export interface GeoTimezone {
  zoneName?: string;
  gmtOffset?: number;
  gmtOffsetName?: string;
  abbreviation?: string;
  tzName?: string;
}

export interface GeoCountry {
  id: string;
  name: string;
  iso2: string;
  emoji: string;
  phone_code?: string;
  iso3?: string;
  region?: string;
  subregion?: string;
  capital?: string;
  currency?: string;
  currency_name?: string;
  currency_symbol?: string;
  native?: string;
  nationality?: string;
  tld?: string;
  /** IANA timezones for the country, parsed from the seeded JSON string. */
  timezones?: GeoTimezone[];
  /** BCP 47 tags for the country (e.g. ["es-ES","ca-ES"]), seeded from countries.csv. */
  language_codes?: string[];
}

export interface GeoState {
  id: string;
  name: string;
  country_code: string;
  state_code?: string;
  type?: string;
  latitude?: string;
  longitude?: string;
  timezone?: string;
}

export interface GeoCity {
  id: string;
  name: string;
  state_code?: string;
  country_code?: string;
  latitude?: string;
  longitude?: string;
  timezone?: string;
}

/**
 * Parse the seeded `timezones` column. The seeder stores
 * `JSON.stringify(timezones)` (an array of { zoneName, gmtOffset, … }), but
 * guard against objects and malformed JSON so reads never throw.
 */
const parseCountryTimezones = (raw: unknown): GeoTimezone[] | undefined => {
  if (Array.isArray(raw)) return raw as GeoTimezone[];
  if (typeof raw !== 'string' || !raw.trim()) return undefined;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as GeoTimezone[]) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Parse the `language_codes` column — a comma-separated list of BCP 47 tags
 * ("es-ES,ca-ES,eu-ES,gl-ES"), seeded from utils/countries.csv. Tolerates
 * arrays (already parsed) and malformed input.
 */
const parseLanguageCodes = (raw: unknown): string[] | undefined => {
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw !== 'string' || !raw.trim()) return undefined;
  const tags = raw.split(',').map((t) => t.trim()).filter(Boolean);
  return tags.length > 0 ? tags : undefined;
};

/**
 * In-flight Promise caches for the geo collections.
 *
 * Geo reference data is static, so concurrent calls for the SAME key (several
 * components mounting and all calling listCountries / listStates('ES') at
 * once) must share ONE network request: the first caller stores its Promise
 * and every later caller awaits it instead of re-fetching. Resolved values
 * are cached for the session; failures are NOT cached, so a retry re-fires
 * (matches the throw-on-transient-errors pattern — see memory
 * `catch-return-fallback-caches-false`).
 *
 * The countries walk is ~10 paginated requests (250 countries ÷ 25/page) and
 * the boot prefetch fires it on EVERY app start — a pure reload cost. The
 * completed walk is therefore mirrored to localStorage and hydrated on boot,
 * so a warm session skips all 10 requests. Cleared when the storage read
 * fails or the shape doesn't validate; always re-verified by a background
 * revalidate-while-stale-style refresh is unnecessary — countries.csv seeds
 * change only when we re-seed the collection.
 */
const COUNTRIES_STORAGE_KEY = 'brainiacs-geo-countries-v1';

const readPersistedCountries = (): GeoCountry[] | null => {
  try {
    const raw = localStorage.getItem(COUNTRIES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Minimal shape validation — an array of objects with name + iso2.
    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed.every((c) => c && typeof c === 'object' && typeof c.name === 'string' && typeof c.iso2 === 'string')
    ) {
      return parsed as GeoCountry[];
    }
    localStorage.removeItem(COUNTRIES_STORAGE_KEY);
    return null;
  } catch {
    return null;
  }
};

const persistCountries = (countries: GeoCountry[]): void => {
  try {
    localStorage.setItem(COUNTRIES_STORAGE_KEY, JSON.stringify(countries));
  } catch {
    // Quota errors etc. — session cache still works, persistence just skips.
  }
};

const countriesCache = createAsyncRequestCache<GeoCountry[]>({
  ttlMs: Number.MAX_SAFE_INTEGER,
});
const statesCache = createAsyncRequestCache<GeoState[]>({
  ttlMs: Number.MAX_SAFE_INTEGER,
});
const citiesCache = createAsyncRequestCache<GeoCity[]>({
  // An empty array is a valid resolution (state with no seeded cities), so
  // cache it — otherwise we'd re-query an empty result on every mount.
  ttlMs: Number.MAX_SAFE_INTEGER,
  shouldCache: () => true,
});

/** Paginated listDocuments — this Appwrite build caps page size at 25 no
 *  matter the requested limit, so every "load all" query must walk pages via
 *  cursor until exhausted. Shared by countries/states/cities. */
const listAllDocuments = async (
  collectionId: string,
  buildQueries: (cursor?: string) => string[],
): Promise<Record<string, any>[]> => {
  const docs: Record<string, any>[] = [];
  let cursor: string | undefined;
  do {
    const result = await databases.listDocuments(
      DATABASE_ID,
      collectionId,
      buildQueries(cursor),
    );
    docs.push(...(result.documents as Record<string, any>[]));
    cursor = result.documents.length > 0
      ? result.documents[result.documents.length - 1].$id
      : undefined;
    if (result.documents.length < 25) break;
  } while (cursor);
  return docs;
};

export const geoService = {
  listCountries(): Promise<GeoCountry[]> {
    return countriesCache.get('all', async () => {
      // Serve the persisted walk when present — skips the ~10-request
      // pagination walk on every reload. The collection is only re-seeded
      // manually, so persisted data stays valid across sessions.
      const persisted = readPersistedCountries();
      if (persisted) return persisted;

      const documents = await listAllDocuments(COLLECTION_COUNTRIES, (cursor) => {
        const queries = [Query.orderAsc('name'), Query.orderAsc('$id'), Query.limit(25)];
        return cursor ? [...queries, Query.cursorAfter(cursor)] : queries;
      });

      const mapped = documents.map((doc) => ({
        id: doc.$id,
        name: doc.name,
        iso2: doc.iso2,
        emoji: doc.emoji || '',
        phone_code: doc.phone_code || undefined,
        iso3: doc.iso3 || undefined,
        region: doc.region || undefined,
        subregion: doc.subregion || undefined,
        capital: doc.capital || undefined,
        currency: doc.currency || undefined,
        currency_name: doc.currency_name || undefined,
        currency_symbol: doc.currency_symbol || undefined,
        native: doc.native || undefined,
        nationality: doc.nationality || undefined,
        tld: doc.tld || undefined,
        timezones: parseCountryTimezones(doc.timezones),
        language_codes: parseLanguageCodes(doc.language_codes),
      }));

      persistCountries(mapped);
      return mapped;
    });
  },

  listStates(countryCode: string): Promise<GeoState[]> {
    return statesCache.get(countryCode, async () => {
      // Paginated (25/page server cap) — see listAllDocuments. Without this,
      // provinces past page one (e.g. Madrid in ES) never load and their city
      // dropdown stays permanently disabled.
      const documents = await listAllDocuments(COLLECTION_STATES, (cursor) => {
        const queries = [
          Query.equal('country_code', countryCode),
          Query.orderAsc('name'),
          Query.orderAsc('$id'),
          Query.limit(25),
        ];
        return cursor ? [...queries, Query.cursorAfter(cursor)] : queries;
      });

      return documents.map((doc) => ({
        id: doc.$id,
        name: doc.name,
        country_code: doc.country_code,
        state_code: doc.state_code || undefined,
        type: doc.type || undefined,
        latitude: doc.latitude || undefined,
        longitude: doc.longitude || undefined,
        timezone: doc.timezone || undefined,
      }));
    });
  },

  listCities(stateCode: string, countryCode: string): Promise<GeoCity[]> {
    const cacheKey = `${countryCode}:${stateCode}`;
    return citiesCache.get(cacheKey, async () => {
      // Paginated (25/page server cap) — a state like Madrid has ~190 cities;
      // larger states exceed 1000, so the walk continues until exhausted.
      const documents = await listAllDocuments(COLLECTION_CITIES, (cursor) => {
        const queries = [
          Query.equal('state_code', stateCode),
          Query.equal('country_code', countryCode),
          Query.orderAsc('name'),
          Query.orderAsc('$id'),
          Query.limit(25),
        ];
        return cursor ? [...queries, Query.cursorAfter(cursor)] : queries;
      });

      return documents.map((doc) => ({
        id: doc.$id,
        name: doc.name,
        state_code: doc.state_code || undefined,
        country_code: doc.country_code || undefined,
        latitude: doc.latitude || undefined,
        longitude: doc.longitude || undefined,
        timezone: doc.timezone || undefined,
      }));
    });
  },

  /**
   * Best-effort state_code for a city name within a country — the fallback
   * when the stored state VALUE doesn't match any geo_states row (native
   * names like "Cataluña" vs canonical "Catalonia", "ES-M" ISO forms, or
   * empty). Leads almost always carry a resolvable city, so the City dropdown
   * keeps working even when the state string can't be matched. Exact-name
   * match only; same-named cities across states resolve to the first.
   *
   * Caches the resolution under a `state:…` key (a sentinel empty array means
   * "no match") so repeated lookups for the same city don't re-query.
   */
  stateForCity(countryCode: string, cityName: string): Promise<string | null> {
    const cacheKey = `state:${countryCode}:${cityName}`;
    return citiesCache.get(cacheKey, async () => {
      const documents = await listAllDocuments(COLLECTION_CITIES, (cursor) => {
        const queries = [
          Query.equal('country_code', countryCode),
          Query.equal('name', cityName),
          Query.orderAsc('$id'),
          Query.limit(25),
        ];
        return cursor ? [...queries, Query.cursorAfter(cursor)] : queries;
      });

      const stateCode = documents[0]?.state_code || null;
      // Sentinel entry — a one-element array carrying the resolved state_code
      // (or an empty array meaning "no match"); cached by shouldCache above.
      return stateCode
        ? [{ id: cacheKey, name: cityName, state_code: stateCode }]
        : [];
    }).then((sentinel) => (sentinel.length > 0 ? sentinel[0].state_code || null : null));
  },

  clearCache(): void {
    countriesCache.clear();
    statesCache.clear();
    citiesCache.clear();
    try {
      localStorage.removeItem(COUNTRIES_STORAGE_KEY);
    } catch { /* noop */ }
  },

  /**
   * Resolve the primary IANA timezone for a country (loads the country cache
   * if needed). Countries usually have a single zone; multi-zone countries
   * (US, RU…) fall back to the first seeded entry (the capital's zone).
   */
  async primaryTimezoneForCountry(iso2: string): Promise<string | null> {
    const countries = await this.listCountries();
    const zones = countries.find((c) => c.iso2 === iso2)?.timezones;
    return zones?.[0]?.zoneName || null;
  },

  /** ISO currency code (e.g. "EUR") for a country, from geo_countries.currency. */
  async currencyForCountry(iso2: string): Promise<string | null> {
    const countries = await this.listCountries();
    return countries.find((c) => c.iso2 === iso2)?.currency || null;
  },

  /**
   * Full country NAME (e.g. "Spain") for an ISO2 code — the canonical value
   * for the `country` pref key (the ISO2 code goes in `country_code`).
   */
  async countryName(iso2: string): Promise<string | null> {
    const countries = await this.listCountries();
    return countries.find((c) => c.iso2 === iso2)?.name || null;
  },

  /**
   * Primary BCP 47 locale for a country — the FIRST tag in the country's
   * `language_codes` column (e.g. ES → "es-ES"). When the tag isn't in the
   * app's supported-locale set, normalizeLocale maps it to the closest
   * supported tag (see constants/locales).
   */
  async primaryLocale(iso2: string): Promise<string | null> {
    const countries = await this.listCountries();
    const primary = countries.find((c) => c.iso2 === iso2)?.language_codes?.[0];
    return primary ? normalizeLocale(primary) : null;
  },

  /**
   * Resolve a country NAME to its ISO2 code (e.g. "Spain" → "ES"). The
   * reverse of countryName(). Case-insensitive, matches on full name only.
   * Returns null when no match is found.
   */
  async countryIso2(name: string): Promise<string | null> {
    const countries = await this.listCountries();
    const match = countries.find(
      (c) => c.name.toLowerCase() === name.trim().toLowerCase(),
    );
    return match?.iso2 || null;
  },

  /**
   * IANA timezone for a specific state (loads the state list for its country).
   * Falls back to the country's primary zone when the state row has no
   * timezone (small countries seeded without state zones).
   */
  async timezoneForState(countryIso2: string, stateName: string): Promise<string | null> {
    const states = await this.listStates(countryIso2);
    const state = states.find((s) => s.name === stateName);
    if (state?.timezone) return state.timezone;
    return this.primaryTimezoneForCountry(countryIso2);
  },
};
